import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addKeywords } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

export default function AddKeywordsDialog({ websiteId, remaining, disabled }) {
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState("");
  const qc = useQueryClient();

  const parsed = raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const mut = useMutation({
    mutationFn: () => addKeywords(websiteId, parsed),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["keywords", websiteId] });
      qc.invalidateQueries({ queryKey: ["website", websiteId] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success(`${data.inserted} keyword(s) added`);
      setOpen(false);
      setRaw("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to add keywords"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="open-add-keywords"
          disabled={disabled || remaining <= 0}
          className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white px-4 h-10 flex items-center gap-2"
        >
          <Plus size={16} weight="bold" />
          Add keywords
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border border-[var(--rt-border)] bg-white max-w-2xl">
        <DialogHeader>
          <div className="label-eyebrow mb-2">/ bulk add</div>
          <DialogTitle className="font-display text-2xl">Add keywords</DialogTitle>
          <DialogDescription className="text-sm text-[var(--rt-text-muted)]">
            Paste keywords one per line or comma-separated. Duplicates are ignored.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label className="label-eyebrow">Paste one per line — or comma separated</Label>
          <Textarea
            data-testid="keywords-textarea"
            rows={10}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            className="rounded-none border-[var(--rt-border)] focus:border-[var(--rt-primary)] font-mono-data text-sm"
            placeholder={"best coffee beans\nespresso machine review\ncold brew recipe"}
          />
          <div className="flex items-center justify-between text-xs text-[var(--rt-text-muted)]">
            <span>
              <span className="font-mono-data text-[var(--rt-text)]">{parsed.length}</span> parsed ·{" "}
              <span className="font-mono-data text-[var(--rt-text)]">{remaining}</span> slots remaining
            </span>
            {parsed.length > remaining && (
              <span className="text-[var(--rt-warning)]">Only first {remaining} will be added.</span>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" className="rounded-none" onClick={() => setOpen(false)} data-testid="cancel-add-keywords">
            Cancel
          </Button>
          <Button
            data-testid="submit-add-keywords"
            disabled={parsed.length === 0 || mut.isPending}
            onClick={() => mut.mutate()}
            className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white"
          >
            {mut.isPending ? "Adding…" : `Add ${Math.min(parsed.length, remaining)} keyword(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
